import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { API } from "../config/api.js";
import { get, post, del, put } from "../lib/http.js";
import { format } from "date-fns";
import { StarIcon as SolidStar } from "@heroicons/react/24/solid";
import FancyLoader from "../components/FancyLoader.jsx";
import { FaEdit } from "react-icons/fa";
import { MdDelete } from "react-icons/md";
import AuthModal from "./AuthModal.jsx";
import logo from "../assets/logo2.png";
import { absMediaUrl, formatEventScheduleLabel } from "../api/events.js";

/* ---------- tiny helpers ---------- */
const currencyINR = (n) =>
  typeof n === "number" ? `₹${n.toLocaleString("en-IN")}` : n || "—";

function timeAgo(dateString) {
  const now = new Date();
  const past = new Date(dateString);
  const diff = Math.floor((now - past) / 1000);
  if (diff < 60) return `${diff} seconds ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)} minutes ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hours ago`;
  if (diff < 2592000) return `${Math.floor(diff / 86400)} days ago`;
  if (diff < 31536000) return `${Math.floor(diff / 2592000)} months ago`;
  return `${Math.floor(diff / 31536000)} years ago`;
}

/* ---------- minimal icons ---------- */
function IconBack() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <polyline points="15 18 9 12 15 6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function CalendarIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" strokeWidth="2" />
      <line x1="16" y1="2" x2="16" y2="6" strokeWidth="2" />
      <line x1="8" y1="2" x2="8" y2="6" strokeWidth="2" />
      <line x1="3" y1="10" x2="21" y2="10" strokeWidth="2" />
    </svg>
  );
}
function StarIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="text-amber-500">
      <path d="M12 .587l3.668 7.431L24 9.748l-6 5.854L19.335 24 12 20.201 4.665 24 6 15.602 0 9.748l8.332-1.73z" />
    </svg>
  );
}
function StarHalfIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" className="text-amber-500">
      <defs>
        <linearGradient id="half">
          <stop offset="50%" stopColor="currentColor" />
          <stop offset="50%" stopColor="transparent" />
        </linearGradient>
      </defs>
      <path
        d="M12 .587l3.668 7.431L24 9.748l-6 5.854L19.335 24 12 20.201 4.665 24 6 15.602 0 9.748l8.332-1.73z"
        fill="url(#half)"
      />
    </svg>
  );
}
function MapPinIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 1 1 18 0z" strokeWidth="2" />
      <circle cx="12" cy="10" r="3" strokeWidth="2" />
    </svg>
  );
}
function UsersIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <path d="M17 21v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2" strokeWidth="2" />
      <circle cx="9" cy="7" r="4" strokeWidth="2" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" strokeWidth="2" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" strokeWidth="2" />
    </svg>
  );
}
function ArrowRight() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <line x1="5" y1="12" x2="19" y2="12" strokeWidth="2" />
      <polyline points="12 5 19 12 12 19" strokeWidth="2" />
    </svg>
  );
}
function HeartIcon({ filled }) {
  return filled ? (
    <svg width="16" height="16" viewBox="0 0 24 24" className="text-rose-600" fill="currentColor">
      <path d="M12 21s-6.716-4.646-9.193-7.122C-.86 11.045.38 6.3 4.243 5.343A4.997 4.997 0 0 1 12 7c1.657-2.875 5.657-2.657 7.757-.557 3.864.957 5.103 5.702 1.436 8.535C18.716 16.354 12 21 12 21z" />
    </svg>
  ) : (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="text-rose-600" stroke="currentColor">
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 1 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78z" strokeWidth="2" />
    </svg>
  );
}

const toISTDate = (input) => {
  if (!input) return null;
  if (input instanceof Date) return input;
  const s = String(input);
  const fixed = /Z$/i.test(s) ? s.replace(/Z$/i, "+05:30") : s;
  return new Date(fixed);
};

export default function EventDetailsPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const location = useLocation()

  const [events, setEvents] = useState(null);
  const [selectedKey, setSelectedKey] = useState(null);

  const [showModal, setShowModal] = useState(false);
  const [stars, setStars] = useState(0);
  const [hoverStars, setHoverStars] = useState(0);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);

  const [reviews, setReviews] = useState([]);
  const [rating, setRating] = useState({ value: 0, count: 0 });

  const [showAllReviews, setShowAllReviews] = useState(false);
  const [showPopup, setShowPopup] = useState(false);

  const [slideIdx, setSlideIdx] = useState(0);
  const touchRef = useRef({ x: 0 });

  const [editingReview, setEditingReview] = useState(null); // review being edited

  // ---- NEW: interested state ----
  const [isInterested, setIsInterested] = useState(false);
  const [interestCount, setInterestCount] = useState(0);
  const [interestBusy, setInterestBusy] = useState(false);

  /* ---------------- current user (from localStorage) ---------------- */
  const currentUser = useMemo(() => {
    try {
      const raw = JSON.parse(localStorage.getItem("bb_user"));
      if (!raw) return null;
      // normalize shape: prefer parsed.id then parsed._id
      const id = raw?.id ?? raw?._id ?? raw?.userId ?? null;
      const isAdmin = raw?.isAdmin || raw?.role === "admin" || raw?.roles?.includes?.("admin");
      return { raw: raw, id, isAdmin, token: raw?.token };
    } catch (e) {
      return null;
    }
  }, []);

  /* ---------------- helper: has current user already posted a review ---------------- */
  const userHasReviewed = useMemo(() => {
    if (!currentUser?.id || !reviews?.length) return false;
    return reviews.some((r) => String(r.userId?._id ?? r.userId) === String(currentUser.id));
  }, [currentUser, reviews]);

  const isAdmin = currentUser?.isAdmin === true;

  const images = Array.isArray(events?.landsacpImage)
    ? events.landsacpImage.filter(Boolean)
    : events?.landsacpImage
      ? [events.landsacpImage]
      : [];

  useEffect(() => {
    if (images.length <= 1) return;
    const t = setInterval(() => setSlideIdx((i) => (i + 1) % images.length), 3500);
    return () => clearInterval(t);
  }, [images.length]);

  const goPrev = () => setSlideIdx((i) => (i - 1 + images.length) % images.length);
  const goNext = () => setSlideIdx((i) => (i + 1) % images.length);
  const onTouchStart = (e) => (touchRef.current.x = e.touches[0].clientX);
  const onTouchEnd = (e) => {
    const dx = e.changedTouches[0].clientX - touchRef.current.x;
    if (Math.abs(dx) > 40) (dx > 0 ? goPrev() : goNext());
  };

  /* ---------------- Back button ---------------- */
  const handleBack = () => {
    nav("/");
  };

  const goToCheckout = () => {
    const tierKey = selectedTier?.key ?? selectedTier?.type ?? "";
    const qs = tierKey ? `?tier=${encodeURIComponent(tierKey)}` : "";
    nav(`/checkout/${events._id}${qs}`);
  };

  /* ---------------- login gate for booking ---------------- */
  const handleClick = () => {
    // Already logged in → straight to checkout (details taken there)
    if (currentUser?.token) {
      goToCheckout();
      return;
    }
    // Guest → open the login popup; after login it navigates to checkout
    const tierKey = selectedTier?.key ?? selectedTier?.type ?? "";
    const qs = tierKey ? `?tier=${encodeURIComponent(tierKey)}` : "";
    openAuth("login", `/checkout/${events._id}${qs}`);
  };
  const handleCancel = () => setShowPopup(false);
  const handleLoginRedirect = () => {
    setShowPopup(false);
    const target = `${location.pathname}${location.search || ""}`;
    // nav(`/login?next=${encodeURIComponent(target)}`, { state: { background: location }});
    openAuth("login", window.location.pathname + window.location.search);
  };
  const [authTab, setAuthTab] = useState("login"); // 'login' | 'register'
  const [nextAfterAuth, setNextAfterAuth] = useState("/");
  const [authOpen, setAuthOpen] = useState(false);
  const openAuth = (tab = "login", next = "/") => {
    setAuthTab(tab);
    setNextAfterAuth(next);
    setAuthOpen(true);
  };

  /* ---------------- ticket tiers ---------------- */
  const availableTiers = useMemo(() => {
    const list = Array.isArray(events?.ticketCategories)
      ? events.ticketCategories
      : [];
    return list.filter((t) => t?.isTicketAvailable !== false);
  }, [events]);

  const cheapestTier = useMemo(() => {
    if (!availableTiers.length) return null;
    const sorted = [...availableTiers].sort(
      (a, b) => Number(a?.price ?? 0) - Number(b?.price ?? 0)
    );
    return sorted[0] ?? null;
  }, [availableTiers]);

  const selectedTier = useMemo(() => {
    if (!availableTiers.length) return null;
    const found = availableTiers.find(
      (t) => (t.key ?? t.type) === selectedKey
    );
    if (found) return found;
    return cheapestTier;
  }, [availableTiers, selectedKey, cheapestTier]);

  const startingFrom = useMemo(() => {
    return cheapestTier ? Number(cheapestTier.price || 0) : 0;
  }, [cheapestTier]);

  /* ---------------- initial data ---------------- */
  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const token = currentUser?.token;
        const res = await get(`${API.events.getSinglEvent}/${id}`, {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
            "Content-Type": "application/json",
          },
        });
        if (!isMounted || !res?.event) return;
        const ev = res.event;
        setEvents(ev);
        const firstAvailable =
          (ev.ticketCategories || []).find((t) => Number(t.availableSeats ?? 0) > 0) ||
          ev.ticketCategories?.[0];
        const firstKey = firstAvailable ? firstAvailable.key ?? firstAvailable.type : null;
        setSelectedKey(firstKey);
      } catch (err) {
        if (isMounted) console.error("getSinglEvent error:", err);
      }
    })();
    return () => { isMounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  /* ---------------- interest: count + my status ---------------- */
  useEffect(() => {
    if (!events?._id) return;
    (async () => {
      try {
        const cntRes = await get(API.interest.count(events._id));
        setInterestCount(Number(cntRes?.count ?? 0));

        const tokenFromState = currentUser?.token;
        const tokenFromStorage = JSON.parse(localStorage?.getItem('bb_user'))

        const token = tokenFromState ?? tokenFromStorage?.token;
        if (token) {
          const my = await get(API.interest.my, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const found = Array.isArray(my?.events)
            ? my.events.some((ev) => String(ev._id) === String(events._id))
            : false;
          setIsInterested(found);
        } else {
          setIsInterested(false);
        }
      } catch (e) {
        console.error("interest init error:", e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events?._id]);

  const toggleInterested = async () => {
    // const token = currentUser?.token;
    const tokenFromState = currentUser?.token;
    const tokenFromStorage = JSON.parse(localStorage?.getItem('bb_user'))

    const token = tokenFromState ?? tokenFromStorage?.token;

    if (!token) {
      setShowPopup(true);
      return;
    }
    if (!events?._id || interestBusy) return;

    setInterestBusy(true);
    const prev = isInterested;
    // optimistic update
    setIsInterested(!prev);
    setInterestCount((c) => (prev ? Math.max(0, c - 1) : c + 1));

    try {
      if (!prev) {
        // mark
        await post(
          API.interest.mark(events._id),
          { notifyOn: ["tickets_live", "reminder"], source: "web" },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        // unmark
        await del(API.interest.unmark(events._id), {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
      }
    } catch (err) {
      console.error("toggleInterested error:", err);
      // rollback on error
      setIsInterested(prev);
      setInterestCount((c) => (prev ? c + 1 : Math.max(0, c - 1)));
      alert(
        err?.response?.data?.message ||
        err?.message ||
        "Failed to update interest"
      );
    } finally {
      setInterestBusy(false);
    }
  };

  /* ---------------- reviews + average ---------------- */
  const loadReviews = useCallback(async () => {
    try {
      // const token = currentUser?.token;
      const tokenFromState = currentUser?.token;
      const tokenFromStorage = JSON.parse(localStorage?.getItem('bb_user'))

      const token = tokenFromState ?? tokenFromStorage?.token;
      const res = await get(`${API.review.getReviews}/${id}`, {
        headers: { Authorization: token ? `Bearer ${token}` : undefined, "Content-Type": "application/json" },
      });
      if (res?.reviews) setReviews(res.reviews);
    } catch (err) {
      console.error("getReviews error:", err);
    }
  }, [id, currentUser?.token]);

  const loadAverageRating = useCallback(async () => {
    try {
      // const token = currentUser?.token;
      const tokenFromState = currentUser?.token;
      const tokenFromStorage = JSON.parse(localStorage?.getItem('bb_user'))

      const token = tokenFromState ?? tokenFromStorage?.token;
      const res = await get(`${API.review.averageRating}/${id}`, {
        headers: { Authorization: token ? `Bearer ${token}` : undefined, "Content-Type": "application/json" },
      });
      setRating({ value: res?.averageRating ?? 0, count: res?.reviewCount ?? 0 });
    } catch (err) {
      console.error("averageRating error:", err);
    }
  }, [id, currentUser?.token]);

  useEffect(() => {
    (async () => {
      await loadReviews();
      await loadAverageRating();
    })();
  }, [loadReviews, loadAverageRating]);

  /* ---------------- venue/map derived ---------------- */
  const venue = events?.venue || {};
  const lat = useMemo(() => (Number.isFinite(Number(venue.lat)) ? Number(venue.lat) : null), [venue.lat]);
  const lng = useMemo(() => {
    const raw = venue.lon ?? venue.lng;
    return Number.isFinite(Number(raw)) ? Number(raw) : null;
  }, [venue.lon, venue.lng]);

  const fullAddress = useMemo(
    () => [venue.address, venue.city, venue.state].filter(Boolean).join(", "),
    [venue.address, venue.city, venue.state]
  );
  const placeQuery = useMemo(() => encodeURIComponent(fullAddress || venue.city || venue.name || "India"), [fullAddress, venue.city, venue.name]);
  const hasCoords = Number.isFinite(lat) && Number.isFinite(lng);
  const hasPlace = Boolean(fullAddress || venue.city || venue.name);

  const [mapMode, setMapMode] = useState("place");
  const placeEmbedSrc = hasCoords
    ? `https://www.google.com/maps?q=${lat},${lng}&z=15&output=embed`
    : `https://www.google.com/maps?q=${placeQuery}&z=12&output=embed`;
  const directionsEmbedSrc = hasCoords
    ? `https://www.google.com/maps?output=embed&dirflg=d&saddr=My+Location&daddr=${lat},${lng}`
    : `https://www.google.com/maps?output=embed&dirflg=d&saddr=My+Location&daddr=${placeQuery}`;
  const mapSrc = mapMode === "directions" ? directionsEmbedSrc : placeEmbedSrc;

  const handleGetDirections = (e) => {
    e.preventDefault();
    if (!hasCoords && !hasPlace) return;
    setMapMode("directions");
  };

  /* ---------------- loading state ---------------- */
  if (!events) {
    return (
      <main className="w-full px-4 py-10 grid place-items-center">
        <FancyLoader label="Processing..." />
      </main>
    );
  }

  /* ---------- helper: open review modal (create) ---------- */
  const openCreateReviewModal = () => {
    if (!currentUser?.token) {
      setShowPopup(true);
      return;
    }
    if (userHasReviewed) {
      alert("You have already submitted a review for this event.");
      return;
    }
    setEditingReview(null);
    setStars(0);
    setText("");
    setShowModal(true);
  };

  /* ---------- helper: open edit modal ---------- */
  const openEditReviewModal = (review) => {
    setShowAllReviews(false);
    if (!currentUser?.token) {
      setShowPopup(true);
      return;
    }
    const userId = currentUser?.id;
    const isOwner = userId && String(review.userId?._id || review.userId) === String(userId);
    if (!isOwner && !isAdmin) {
      alert("You can only edit your own review.");
      return;
    }
    setEditingReview(review);
    setStars(review.rating ?? 0);
    setText(review.comment ?? "");
    setShowModal(true);
  };

  /* ---------- submit (create or update) ---------- */
  const handleSubmitReview = async () => {

    if (!stars || !text.trim()) {
      alert("Please give a rating and write a review");
      return;
    }
    try {
      setLoading(true);
      // const token = currentUser?.token;
      const tokenFromState = currentUser?.token;
      const tokenFromStorage = JSON.parse(localStorage?.getItem('bb_user'))

      const token = tokenFromState ?? tokenFromStorage?.token;
      const userId = currentUser?.id;
      if (!token) {
        alert("Login required");
        setShowPopup(true);
        return;
      }

      if (editingReview) {
        const payload = { eventId: events._id, userId, rating: stars, comment: text };
        const res = await put(`${API.review.editReviews}/${editingReview._id}`, payload, {
          headers: { Authorization: token ? `Bearer ${token}` : undefined, "Content-Type": "application/json" },
        });
        // if backend returns success message or updated object, refresh list
        await Promise.all([loadReviews(), loadAverageRating()]);
        setShowModal(false);
        setEditingReview(null);
        setStars(0);
        setText("");
      } else {
        const payload = { eventId: events._id, userId, rating: stars, comment: text };
        await post(API.review.create, payload, {
          headers: { Authorization: token ? `Bearer ${token}` : undefined, "Content-Type": "application/json" },
        });
        await Promise.all([loadReviews(), loadAverageRating()]);
        setShowModal(false);
        setStars(0);
        setText("");
      }
    } catch (err) {
      console.error("Error submitting review", err);
      const serverMsg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Failed to submit review";
      alert(serverMsg);
    } finally {
      setLoading(false);
    }
  };

  /* ---------- delete review ---------- */
  const handleDeleteReview = async (review) => {
    if (!currentUser?.token) {
      setShowPopup(true);
      return;
    }
    const userId = currentUser?.id;
    const isOwner = userId && String(review.userId?._id || review.userId) === String(userId);
    if (!isOwner && !isAdmin) {
      alert("You can only delete your own review.");
      return;
    }
    if (!confirm("Are you sure you want to delete this review?")) return;

    try {
      setLoading(true);
      const res = await del(`${API.review.deleteReviews}/${review._id}`, {
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${currentUser.token}` },
      });
      if (res?.success || res?.ok || res?.deleted) {
        setReviews((prev) => prev.filter((r) => String(r._id) !== String(review._id)));
        await loadAverageRating();
      } else {
        // If backend returns a message
        await loadReviews();
        await loadAverageRating();
      }
    } catch (err) {
      console.error("Delete review error:", err);
      alert(err?.message || "Failed to delete review");
    } finally {
      setLoading(false);
    }
  };

  /* ---------- helper render stars ---------- */
  const renderStars = (val) => {
    const arr = [];
    const full = Math.floor(val);
    const half = val % 1 >= 0.5;
    for (let i = 0; i < full; i++) arr.push(<StarIcon key={`f-${i}`} />);
    if (half) arr.push(<StarHalfIcon key="half" />);
    while (arr.length < 5) arr.push(<StarIcon key={`e-${arr.length}`} />);
    return arr;
  };

  const EmptyReviews = () => (
    <div className="rounded-2xl bg-white border shadow-sm p-8 text-center">
      <div className="text-2xl font-semibold mb-2">No reviews yet</div>
      <p className="text-sm text-slate-600 mb-4">Be the first to share your experience — customers value honest feedback.</p>
      <div className="flex items-center justify-center gap-3">
        <button onClick={openCreateReviewModal} className="rounded-xl bg-amber-400 px-4 py-2 font-semibold text-slate-900">
          Write the first review
        </button>
      </div>
    </div>
  );

  const isPastEvent = events?.status?.toLowerCase() === "past";


  /* =================================================== UI =================================================== */
  return (
    <>
      {/* HERO + SLIDER */}
      <section className="relative w-full">
        <div className="relative w-full md:h-[520px] overflow-visible md:overflow-hidden">
          {/* <div
            className="md:absolute md:inset-0"
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
          >
            <div
              className="w-full flex transition-transform duration-700 ease-out"
              style={{ transform: `translateX(-${slideIdx * 100}%)` }}
            >
              {(Array.isArray(events?.landsacpImage) ? events.landsacpImage : [events?.landsacpImage])
                .filter(Boolean)
                .map((img, i) => (
                  <img
                    key={i}
                    src={img}
                    alt={`${events?.name || "Event"}-${i}`}
                    className="w-full h-auto md:h-[520px] object-cover flex-shrink-0"
                    loading={i === 0 ? "eager" : "lazy"}
                  />
                ))}
            </div>
          </div> */}

          <div
            className="relative w-full overflow-hidden md:inset-0"
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
          >
            <div
              className="w-full flex flex-nowrap transition-transform duration-700 ease-out"
              style={{ transform: `translateX(-${slideIdx * 100}%)` }}
            >
              {(Array.isArray(events?.landsacpImage) ? events.landsacpImage : [events?.landsacpImage])
                .filter(Boolean)
                .map((img, i) => (
                  <div
                    key={i}
                    className="min-w-full flex-shrink-0"
                    aria-hidden={i !== slideIdx}
                  >
                    <img
                      src={absMediaUrl(img) || img}
                      alt=""
                      className="h-[260px] w-full object-cover object-center md:h-[520px]"
                      loading={i === 0 ? "eager" : "lazy"}
                    />
                  </div>
                ))}
            </div>
          </div>

          <div
            className="pointer-events-none absolute inset-x-0 bottom-0 z-[1] hidden h-44 bg-gradient-to-t from-black/75 via-black/30 to-transparent md:block"
            aria-hidden
          />

          <div className="absolute left-4 top-4 z-10">
            <button
              onClick={handleBack}
              className="inline-flex items-center gap-2 rounded-full bg-white/95 px-3 py-1.5 text-sm font-semibold text-slate-800 hover:bg-white shadow"
              title="Back"
            >
              <IconBack /> Back
            </button>
          </div>

          {((Array.isArray(events?.landsacpImage) ? events.landsacpImage.length : 0) > 1) && (
            <>
              <button
                onClick={goPrev}
                className="hidden md:block absolute left-2 top-1/2 -translate-y-1/2 z-10 rounded-full bg-white/90 px-2 py-1 text-sm font-semibold text-slate-900 hover:bg-white shadow"
                aria-label="Previous"
              >
                ‹
              </button>
              <button
                onClick={goNext}
                className="hidden md:block absolute right-2 top-1/2 -translate-y-1/2 z-10 rounded-full bg-white/90 px-2 py-1 text-sm font-semibold text-slate-900 hover:bg-white shadow"
                aria-label="Next"
              >
                ›
              </button>

              <div className="hidden md:flex absolute z-10 right-4 top-4 gap-1">
                {events.landsacpImage.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setSlideIdx(i)}
                    className={`h-2 w-2 rounded-full transition ${slideIdx === i ? "bg-white" : "bg-white/50 hover:bg-white/80"}`}
                    aria-label={`Go to slide ${i + 1}`}
                  />
                ))}
              </div>
            </>
          )}
        </div>

        <div className="relative z-10 mt-3 p-4 font-sans text-slate-900 md:absolute md:inset-x-0 md:bottom-0 md:mt-0 md:p-6 md:text-white">
          <div className="mx-auto max-w-7xl">
            <div className="mb-1 flex flex-wrap items-center gap-2">
              {events?.category && (
                <span className="rounded-lg bg-amber-400 px-2 py-0.5 text-[11px] font-bold text-slate-900 shadow-sm md:shadow-none">
                  {events.category}
                </span>
              )}
              {events?.organizer?.name && (
                <span className="rounded-lg bg-slate-100 px-2 py-0.5 text-[11px] font-bold text-slate-800 md:bg-white/25 md:text-white md:ring-1 md:ring-white/30">
                  By {events.organizer.name}
                </span>
              )}
            </div>

            <h1 className="text-2xl font-black tracking-tight drop-shadow md:text-4xl md:drop-shadow-lg">
              {events?.name}
            </h1>

            <div className="mt-2 flex flex-wrap items-center gap-3 text-sm text-slate-700 md:text-white/95">
              {events?.venue && (
                <span className="inline-flex items-center gap-1">
                  <MapPinIcon /> {`${events.venue.name} ${events.venue.city || ""}`}
                </span>
              )}

              {(events?.date || events?.showtimes?.length) && (
                <span className="inline-flex items-center gap-1">
                  <CalendarIcon />
                  {formatEventScheduleLabel(events)}
                </span>
              )}

              <span className="inline-flex items-center gap-1">
                <StarIcon /> {rating.value?.toFixed(1)}
                <span className="opacity-80">({Number(rating.count || 0).toLocaleString()})</span>
              </span>

              <button
                onClick={toggleInterested}
                disabled={interestBusy}
                className={`ml-auto inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-semibold shadow border
                      bg-white text-slate-800
                      md:bg-white/90
                      ${isInterested ? "bg-rose-100 text-rose-700 border-rose-200" : ""}`}
                title={isInterested ? "Remove interest" : "I'm interested"}
              >
                <HeartIcon filled={!!isInterested} />
                {isInterested ? "Interested" : "I'm Interested"}
                <span className="text-xs opacity-70">• {interestCount}</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section className="w-full bg-white font-sans">
        <div className="mx-auto max-w-7xl px-4 py-8 md:px-6">
          <h3 className="text-xl font-bold tracking-tight text-slate-900 md:text-2xl">About this event</h3>
          {events?.description && (
            <div
              className="mt-4 max-w-3xl text-sm leading-relaxed text-slate-700 md:text-base [&_a]:font-semibold [&_a]:text-amber-700 [&_a]:underline [&_p]:my-2 [&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5"
              dangerouslySetInnerHTML={{ __html: events.description }}
            />
          )}
        </div>
      </section>

      {/* TICKETS */}
      <section className="w-full border-t border-slate-200/80 bg-slate-50/50 font-sans">
        <div className="mx-auto max-w-7xl px-4 py-8 md:px-6">
          <h3 className="text-xl font-bold tracking-tight text-slate-900 md:text-2xl">Select Tickets</h3>
          <p className="mt-1 text-sm text-slate-600">Choose a category, then use Book Tickets below.</p>
          <div className="mt-6 space-y-4">
            {events?.ticketCategories
              ?.filter((t) => t.isTicketAvailable !== false)
              .map((t, i) => {
                const key = t.key ?? t.type ?? String(i);
                const isSel = selectedKey === key;
                const avail = Number(t.availableSeats ?? 0);

                let badge = { label: "Available", text: "#065f46", bg: "#d1fae5", dot: "#10b981" };
                if (avail === 0)
                  badge = { label: "Sold out", text: "#b91c1c", bg: "#fee2e2", dot: "#ef4444" };
                else if (avail < 20)
                  badge = { label: `Only ${avail} left`, text: "#92400e", bg: "#fef3c7", dot: "#f59e0b" };

                return (
                  <div
                    key={key}
                    className={`rounded-2xl border bg-white p-5 shadow-sm transition hover:shadow-md
                    ${isSel && avail > 0 ? "border-amber-400 ring-2 ring-amber-300/80 ring-offset-2 ring-offset-slate-50" : "border-slate-200/90"}
                    ${avail === 0 ? "opacity-80" : ""}`}
                  >
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between sm:gap-6">
                      <div className="min-w-0 flex-1">
                        <div className="text-lg font-bold text-slate-900 md:text-xl">{t.type}</div>
                        {Array.isArray(t.perks) &&
                          t.perks.map((p) => String(p || "").trim()).filter(Boolean).length > 0 && (
                            <ul className="mt-2 space-y-1.5">
                              {t.perks
                                .map((p) => String(p || "").trim())
                                .filter(Boolean)
                                .map((perk, pi) => (
                                  <li
                                    key={`${key}-perk-${pi}`}
                                    className="flex items-start gap-2 text-sm leading-snug text-slate-600"
                                  >
                                    <span
                                      className="mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-[10px] font-bold text-emerald-700"
                                      aria-hidden
                                    >
                                      ✓
                                    </span>
                                    <span>{perk}</span>
                                  </li>
                                ))}
                            </ul>
                          )}
                        <div
                          className="mt-3 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold"
                          style={{ color: badge.text, background: badge.bg }}
                        >
                          <span className="inline-block h-2 w-2 rounded-full" style={{ background: badge.dot }} />
                          {badge.label}
                        </div>
                      </div>

                      <div className="flex shrink-0 flex-row items-center justify-between gap-4 border-t border-slate-100 pt-4 sm:flex-col sm:items-end sm:border-0 sm:pt-0">
                        {!isPastEvent && (
                          <div className="text-2xl font-extrabold tracking-tight text-amber-700">
                            {currencyINR(t.price)}
                          </div>
                        )}

                        {avail === 0 ? (
                          <button
                            className="rounded-xl bg-slate-200 px-5 py-2 text-sm font-bold text-slate-500 cursor-not-allowed"
                            disabled
                          >
                            Sold out
                          </button>
                        ) : isSel ? (
                          <button
                            type="button"
                            className="rounded-xl bg-emerald-600 px-5 py-2 text-sm font-bold text-white shadow-sm"
                          >
                            Selected ✓
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="rounded-xl bg-amber-400 px-5 py-2 text-sm font-bold text-slate-900 shadow-sm transition hover:bg-amber-300"
                            onClick={() => setSelectedKey(key)}
                          >
                            Select
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      </section>

      {/* VENUE + MAP */}
      <section className="w-full border-t border-amber-100/60 bg-gradient-to-b from-slate-50 via-white to-amber-50/40 font-sans">
        <div className="mx-auto max-w-7xl px-4 py-8 md:px-6">
          <h3 className="mb-4 text-xl font-bold tracking-tight text-slate-900 md:text-2xl">Venue details</h3>

          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 md:gap-6">
            {/* LEFT */}
            <div className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-md">
              {/* <img
                src={
                  venue?.logoUrl ||
                  `https://www.google.com/maps?q=${lat},${lng}&z=15&output=embed`
                }
                alt={venue?.name || "Venue"}
                className="h-56 w-full object-cover"
              /> */}
              {
                venue?.logoUrl ? (
                  <img
                    src={absMediaUrl(venue.logoUrl) || venue.logoUrl}
                    alt=""
                    className="h-56 w-full object-cover"
                  />
                ) : venue?.name ? (
                  <div className="flex h-56 w-full items-center justify-center bg-gradient-to-r from-amber-500 via-orange-500 to-amber-600 px-4 text-center text-2xl font-black uppercase leading-tight tracking-tight text-white md:text-3xl">
                    {venue.name}
                  </div>

                ) : (lat && lng) ? (
                  <img
                    src={`https://maps.googleapis.com/maps/api/staticmap?center=${lat},${lng}&zoom=15&size=800x400&markers=color:red|${lat},${lng}`}
                    alt="Map preview"
                    className="h-56 w-full object-cover"
                  />
                ) : (
                  <div className="h-56 w-full bg-slate-200 flex items-center justify-center text-slate-600 text-sm">
                    No Image Available
                  </div>
                )
              }

              <div className="p-4 md:p-5">
                {venue?.name && (
                  <div className="flex items-center gap-2 text-slate-800 font-semibold text-xl">
                    <MapPinIcon /> {venue.name}
                  </div>
                )}
                <div className="text-slate-700 mt-1">{fullAddress || "—"}</div>

                <div className="mt-3 flex items-center justify-between text-sm">
                  <div className="inline-flex items-center gap-2 text-slate-700">
                    <UsersIcon /> Capacity: {venue?.capacity || "—"}
                  </div>

                  {(hasCoords || hasPlace) ? (
                    <button
                      className="inline-flex items-center gap-1 text-amber-700 font-medium hover:underline"
                      onClick={handleGetDirections}
                      title="Switch the map to directions"
                    >
                      Get Directions <ArrowRight />
                    </button>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-slate-400 cursor-not-allowed" title="Location not available">
                      Get Directions <ArrowRight />
                    </span>
                  )}
                </div>

                {!hasCoords && !hasPlace && (
                  <p className="mt-3 text-xs text-amber-700">
                    Tip: add <code>venue.lat</code>/<code>venue.lon</code> or at least <code>address/city</code> to enable the map & directions.
                  </p>
                )}
              </div>
            </div>

            {/* RIGHT */}
            <div className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-md">
              <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
                <div className="text-sm font-semibold text-slate-800">
                  {mapMode === "place" ? "Map" : "Directions"}
                  {!hasCoords && hasPlace ? (
                    <span className="ml-2 text-xs font-normal text-slate-500">(showing: {decodeURIComponent(placeQuery)})</span>
                  ) : null}
                </div>
                {(hasCoords || hasPlace) && (
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setMapMode("place")}
                      className={`rounded-full border px-3 py-1 text-sm font-semibold transition ${mapMode === "place" ? "border-amber-500 bg-amber-400 text-slate-900 shadow-sm" : "border-slate-200 bg-white text-slate-700 hover:border-amber-200"}`}
                    >
                      Pin
                    </button>
                    <button
                      type="button"
                      onClick={() => setMapMode("directions")}
                      className={`rounded-full border px-3 py-1 text-sm font-semibold transition ${mapMode === "directions" ? "border-amber-500 bg-amber-400 text-slate-900 shadow-sm" : "border-slate-200 bg-white text-slate-700 hover:border-amber-200"}`}
                    >
                      Directions
                    </button>
                  </div>
                )}
              </div>

              <div className="aspect-[16/11] md:aspect-[16/10]">
                {(hasCoords || hasPlace) ? (
                  <iframe
                    key={mapSrc}
                    title="Venue Map"
                    src={mapSrc}
                    className="h-full w-full"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    allowFullScreen
                  />
                ) : (
                  <div className="h-full w-full flex items-center justify-center text-sm text-slate-500">
                    Location not available
                  </div>
                )}
              </div>

              {(hasCoords || hasPlace) && (
                <div className="px-4 py-3 border-t text-sm text-slate-600">
                  {hasCoords ? (
                    <>
                      <span className="font-medium">Coords:</span> {lat?.toFixed(6)}, {lng?.toFixed(6)} •{" "}
                      <a className="text-amber-700 hover:underline" href={`https://www.google.com/maps?daddr=${lat},${lng}`} target="_blank" rel="noreferrer">
                        Open in Google Maps
                      </a>
                    </>
                  ) : (
                    <>
                      <span className="font-medium">Location:</span> {decodeURIComponent(placeQuery)} •{" "}
                      <a className="text-amber-700 hover:underline" href={`https://www.google.com/maps?daddr=${placeQuery}`} target="_blank" rel="noreferrer">
                        Open in Google Maps
                      </a>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* TERMS */}
      {Array.isArray(events?.termsAndConditions) && events.termsAndConditions.length > 0 && (
        <section className="w-full bg-white border-t">
          <div className="mx-auto max-w-7xl px-4 md:px-6 py-6">
            <h3 className="font-semibold text-slate-900 text-2xl">Terms & Conditions</h3>
            <ul className="mt-3 space-y-2 text-sm text-slate-700">
              {events.termsAndConditions.map((t, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="mt-1 inline-block h-1.5 w-1.5 rounded-full bg-slate-800" />
                  <span>{t.point}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* REVIEWS */}
      <section className="w-full bg-[#fff8e1] border-t">
        <div className="mx-auto max-w-7xl px-4 md:px-6 py-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-slate-900 text-2xl">Reviews & Ratings</h3>
            {reviews.length > 3 && (
              <button onClick={() => setShowAllReviews(true)} className="text-sm text-amber-700 hover:underline">
                View All
              </button>
            )}
          </div>

          <div className="rounded-2xl bg-white border shadow-sm p-4 md:p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="text-2xl font-bold">{rating.value ? rating.value.toFixed(1) : "0.0"}</div>
                <div className="flex items-center">
                  {renderStars(rating.value)}
                </div>
                <div className="text-sm text-slate-500">
                  {rating.count} {rating.count === 1 ? "review" : "reviews"}
                </div>
              </div>

              {/* show Write Review only if user hasn't reviewed yet */}
              {!userHasReviewed ? (
                <button onClick={openCreateReviewModal} className="rounded-full bg-white border px-4 py-2 text-sm font-semibold">
                  Write Review
                </button>
              ) : (
                <div className="text-sm text-slate-500 italic">You have already reviewed</div>
              )}
            </div>

            <div className="mt-4 divide-y">
              {reviews.length === 0 ? (
                <div className="py-4">
                  <EmptyReviews />
                </div>
              ) : (
                (reviews.slice(0, 3)).map((r, i) => (
                  <div key={r._id || i} className="py-3 flex gap-3">
                    {/* <img
                      src={r.userId?.profileImage }
                      alt={r.userId?.name || "User"}
                      className="h-10 w-10 rounded-full object-cover"
                    /> */}
                    {r.userId?.profileImage ? (
                      <img
                        src={r.userId.profileImage}
                        alt={r.userId?.name || "User"}
                        className="h-10 w-10 rounded-full object-cover"
                      />
                    ) : (
                      <div className="h-10 w-10 rounded-full bg-amber-600 text-white flex items-center justify-center text-sm font-bold uppercase">
                        {r.userId?.name?.substring(0, 2) || "U"}
                      </div>
                    )}

                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="font-semibold">{r.userId?.name || "User"}</div>
                          <div className="text-xs text-slate-500">{timeAgo(r.createdAt)}</div>
                          <div className="ml-3 flex items-center">{renderStars(r.rating || 0)}</div>
                        </div>

                        {/* Edit/Delete for owner's review OR admin */}
                        {(() => {
                          const userId = currentUser?.id;
                          const isOwner = userId && String(r.userId?._id || r.userId) === String(userId);
                          const showActions = isOwner || isAdmin;
                          if (!showActions) return null;
                          return (
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => openEditReviewModal(r)}
                                className="text-xs px-2 py-1 border rounded text-slate-700 hover:bg-slate-50"
                              >
                                <FaEdit />
                              </button>
                              <button
                                onClick={() => handleDeleteReview(r)}
                                className="text-xs px-2 py-1 border rounded text-red-600 hover:bg-red-50"
                                disabled={loading}
                              >
                                {loading ? "Deleting..." : <MdDelete />}
                              </button>
                            </div>
                          );
                        })()}
                      </div>
                      <p className="text-sm text-slate-700 mt-1">{r.comment}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </section>

      {/* WRITE / EDIT REVIEW MODAL */}
      {showModal && (
        <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl p-6 w-[92vw] max-w-md shadow-2xl relative border border-amber-100/70 animate-[popIn_180ms_cubic-bezier(0.22,1,0.36,1)]">
            <button onClick={() => { setShowModal(false); setEditingReview(null); }} className="absolute top-3 right-3 text-slate-500 hover:text-slate-700">✕</button>
            <h3 className="text-lg font-bold mb-4">{editingReview ? "Edit your review" : "Write a Review"}</h3>

            <div className="flex gap-2 mb-4 justify-center">
              {[1, 2, 3, 4, 5].map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setStars(s)}
                  onMouseEnter={() => setHoverStars(s)}
                  onMouseLeave={() => setHoverStars(0)}
                  className="focus:outline-none"
                >
                  <SolidStar className={`h-8 w-8 cursor-pointer transition-colors ${s <= (hoverStars || stars) ? "text-amber-400" : "text-slate-300"}`} />
                </button>
              ))}
            </div>

            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              className="w-full border rounded-lg p-3 text-sm focus:ring-2 focus:ring-amber-300 outline-none"
              placeholder="Write your review here..."
              rows="4"
            />

            <div className="mt-4 flex justify-end gap-3">
              <button onClick={() => { setShowModal(false); setEditingReview(null); }} className="px-4 py-2 rounded-full border text-sm font-semibold">
                Cancel
              </button>
              <button
                onClick={handleSubmitReview}
                disabled={loading || stars === 0}
                className="px-4 py-2 rounded-full bg-amber-400 text-slate-900 font-semibold hover:bg-amber-300 disabled:opacity-50"
              >
                {loading ? "Saving..." : editingReview ? "Save Changes" : "Submit"}
              </button>
            </div>
          </div>

          <style>{`
            @keyframes popIn {
              0% { opacity: 0; transform: translateY(10px) scale(.98) }
              100% { opacity: 1; transform: translateY(0) scale(1) }
            }
          `}</style>
        </div>
      )}

      {/* ALL REVIEWS MODAL */}
      {showAllReviews && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-[92vw] max-w-3xl p-5 relative">
            <button onClick={() => setShowAllReviews(false)} className="absolute top-2 right-2 text-gray-500 hover:text-black">✕</button>
            <h3 className="text-lg font-semibold mb-3">All Reviews</h3>
            <div className="max-h-[500px] overflow-y-auto divide-y">
              {reviews.length === 0 ? (
                <div className="p-6 text-center text-sm text-slate-600">No reviews yet.</div>
              ) : (
                reviews.map((r, i) => (
                  <div key={r._id || i} className="py-3 flex gap-3 items-start">
                    {/* <img
                      src={r.userId?.profileImage || "https://i.pravatar.cc/64?img=8"}
                      alt={r.userId?.name || "User"}
                      className="h-10 w-10 rounded-full object-cover"
                    /> */}
                    {r.userId?.profileImage ? (
                      <img
                        src={r.userId.profileImage}
                        alt={r.userId?.name || "User"}
                        className="h-10 w-10 rounded-full object-cover"
                      />
                    ) : (
                      <div className="h-10 w-10 rounded-full bg-amber-600 text-white flex items-center justify-center text-sm font-bold uppercase">
                        {r.userId?.name?.substring(0, 2) || "U"}
                      </div>
                    )}

                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="font-semibold">{r.userId?.name || "User"}</div>
                          <div className="text-xs text-slate-500">{timeAgo(r.createdAt)}</div>
                          <div className="ml-3 flex items-center">{renderStars(r.rating || 0)}</div>
                        </div>

                        {/* owner or admin actions */}
                        {(() => {
                          const userId = currentUser?.id;
                          const isOwner = userId && String(r.userId?._id || r.userId) === String(userId);
                          const showActions = isOwner || isAdmin;
                          if (!showActions) return null;
                          return (
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => openEditReviewModal(r)}
                                className="text-xs px-2 py-1 border rounded text-slate-700 hover:bg-slate-50"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDeleteReview(r)}
                                className="text-xs px-2 py-1 border rounded text-red-600 hover:bg-red-50"
                                disabled={loading}
                              >
                                {loading ? "Deleting..." : "Delete"}
                              </button>
                            </div>
                          );
                        })()}
                      </div>
                      <p className="text-sm text-slate-700 mt-1">{r.comment}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* STICKY BOTTOM BAR — above MobileBottomNav on small screens */}
      <div aria-hidden className="h-[10rem] md:h-[80px]" />
      {events.status !== 'Past' &&
        <div
          className="fixed inset-x-0 bottom-[4.75rem] z-40 border-t border-amber-100/80 bg-white/95 font-sans shadow-[0_-12px_40px_rgba(0,0,0,0.08)] backdrop-blur-md md:bottom-0 md:z-30"
          style={{ paddingBottom: "max(0.625rem, env(safe-area-inset-bottom, 0px))" }}
        >
          <div className="mx-auto flex w-full max-w-7xl items-center justify-between gap-3 px-4 py-3 md:px-6">
            <div className="min-w-0">
              {selectedTier ? (
                <>
                  <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500">
                    Selected · {selectedTier.type ?? selectedTier.tier ?? "Ticket"}
                  </div>
                  <div className="truncate text-xl font-extrabold tracking-tight text-amber-800">
                    {currencyINR(selectedTier.price)}
                  </div>
                </>
              ) : (
                <>
                  <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Starting from</div>
                  <div className="text-xl font-extrabold text-slate-900">
                    {startingFrom ? currencyINR(startingFrom) : "—"}
                  </div>
                </>
              )}
            </div>

            <div className="flex shrink-0 items-center gap-2">
              <button
                type="button"
                onClick={toggleInterested}
                disabled={interestBusy}
                className={`hidden items-center gap-2 rounded-xl border px-4 py-2 text-sm font-bold md:inline-flex
                ${isInterested ? "border-rose-300 bg-rose-50 text-rose-700" : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"}`}
                title={isInterested ? "Remove interest" : "I'm interested"}
              >
                <HeartIcon filled={isInterested} />
                <span className="max-w-[120px] truncate">Interested · {interestCount}</span>
              </button>

              <button
                type="button"
                onClick={handleBack}
                className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50 md:px-4 md:py-2.5"
              >
                Back
              </button>

              <button
                type="button"
                className={`min-w-[118px] rounded-xl px-4 py-2 text-sm font-bold shadow-sm transition md:min-w-[150px] md:px-5 md:py-2.5 ${selectedTier
                  ? "bg-amber-400 text-slate-900 hover:bg-amber-300"
                  : "cursor-not-allowed bg-slate-200 text-slate-500"
                  }`}
                onClick={() => {
                  if (!selectedTier) return;
                  handleClick();
                }}
                disabled={!selectedTier}
              >
                {selectedTier ? "Book Tickets" : "Unavailable"}
              </button>
            </div>
          </div>
        </div>
      }

      {/* LOGIN POPUP */}
      {showPopup && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          role="dialog"
          aria-modal="true"
          aria-labelledby="login-modal-title"
        >
          <button onClick={handleCancel} aria-label="Close" className="absolute inset-0 bg-black/50 transition-opacity" />
          <div className="relative w-[92vw] max-w-sm rounded-2xl bg-white shadow-2xl animate-[popIn_180ms_cubic-bezier(0.22,1,0.36,1)] border border-amber-100/70">
            <div className="h-1.5 rounded-t-2xl bg-gradient-to-r from-amber-400 via-amber-500 to-amber-400" />
            <div className="p-5">
              <button
                onClick={handleCancel}
                aria-label="Close"
                className="absolute right-2 top-2 rounded-full p-2 hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-300"
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M6 18L18 6M6 6l12 12" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>

              <h2 id="login-modal-title" className="text-lg font-semibold text-slate-900">
                Please log in to continue
              </h2>
              <p className="mt-1 text-sm text-slate-600">You need to be signed in to complete your action.</p>

              <div className="mt-5 flex justify-end gap-3">
                <button
                  onClick={handleCancel}
                  className="rounded-xl border px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleLoginRedirect}
                  className="rounded-xl bg-amber-400 px-4 py-2 text-sm font-semibold text-slate-900 hover:bg-amber-500 shadow-sm"
                >
                  Login
                </button>
              </div>
            </div>
          </div>

          <style>{`
            @keyframes popIn {
              0% { opacity: 0; transform: translateY(10px) scale(.98) }
              100% { opacity: 1; transform: translateY(0) scale(1) }
            }
          `}</style>
        </div>
      )}

      <AuthModal
        open={authOpen}
        onClose={() => setAuthOpen(false)}
        defaultTab={authTab}
        next={nextAfterAuth}
        logoSrc={logo}                         // show company logo at top
        brandName="Show Time Bro"   // fallback name for welcome
        headerVariant="logo"                  // change to "welcome" for "Welcome, <name>"
      />
    </>
  );
}
